import React, { useState } from 'react';
import { Handshake, Building2, Wrench, Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const DevenirPartenaire = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    partnerType: 'revendeur',
    experience: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      await axios.post(`${API_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `TYPE: ${formData.partnerType === 'revendeur' ? 'Devenir Revendeur' : 'Devenir Installateur'}

ENTREPRISE: ${formData.company}
ADRESSE: ${formData.address}
EXPÉRIENCE: ${formData.experience}

MESSAGE:
${formData.message}`,
        subject: `Demande Partenariat - ${formData.partnerType === 'revendeur' ? 'Revendeur' : 'Installateur'}`
      });

      toast({
        title: "Demande envoyée !",
        description: "Nous vous contacterons dans les plus brefs délais.",
        duration: 5000,
      });

      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        address: '',
        partnerType: 'revendeur',
        experience: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer votre demande. Veuillez réessayer.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <Handshake className="w-12 h-12 text-[#00BCD4]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
              Devenir Partenaire Ecopra
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Rejoignez notre réseau de distributeurs et installateurs en Suisse et développez votre activité avec les solutions Ecopra.
          </p>
        </div>

        {/* Partnership Types */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <Card className="border-2 border-[#7CB342] hover:shadow-xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-[#7CB342]/10 to-[#00BCD4]/10">
              <CardTitle className="flex items-center gap-3">
                <Building2 className="text-[#7CB342]" />
                Devenir Revendeur
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#7CB342] flex-shrink-0 mt-1" size={18} />
                  <span className="text-gray-700">Marges attractives sur nos produits</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#7CB342] flex-shrink-0 mt-1" size={18} />
                  <span className="text-gray-700">Support marketing et technique</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#7CB342] flex-shrink-0 mt-1" size={18} />
                  <span className="text-gray-700">Formation produits complète</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#7CB342] flex-shrink-0 mt-1" size={18} />
                  <span className="text-gray-700">Zone géographique protégée</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-2 border-[#00BCD4] hover:shadow-xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-[#7CB342]/10 to-[#00BCD4]/10">
              <CardTitle className="flex items-center gap-3">
                <Wrench className="text-[#00BCD4]" />
                Devenir Installateur
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#00BCD4] flex-shrink-0 mt-1" size={18} />
                  <span className="text-gray-700">Rémunération par installation</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#00BCD4] flex-shrink-0 mt-1" size={18} />
                  <span className="text-gray-700">Formation technique certifiée</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#00BCD4] flex-shrink-0 mt-1" size={18} />
                  <span className="text-gray-700">Missions régulières garanties</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#00BCD4] flex-shrink-0 mt-1" size={18} />
                  <span className="text-gray-700">Support technique permanent</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Contact Form */}
        <Card className="max-w-3xl mx-auto">
          <CardHeader className="bg-gradient-to-r from-[#7CB342]/10 to-[#00BCD4]/10">
            <CardTitle className="text-2xl flex items-center gap-2">
              <Mail className="text-[#00BCD4]" />
              Formulaire de candidature
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Type de partenariat */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de partenariat *
                </label>
                <select
                  name="partnerType"
                  value={formData.partnerType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00BCD4] focus:border-transparent"
                  required
                >
                  <option value="revendeur">Devenir Revendeur</option>
                  <option value="installateur">Devenir Installateur</option>
                </select>
              </div>

              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entreprise
                  </label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Nom de votre entreprise"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contact@entreprise.ch"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+41 79 123 45 67"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse / Région *
                </label>
                <Input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Ville, Canton"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expérience dans le domaine
                </label>
                <Textarea
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  placeholder="Décrivez brièvement votre expérience professionnelle..."
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Votre message
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Parlez-nous de votre projet, vos motivations..."
                  rows={4}
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#7CB342] to-[#00BCD4] hover:from-[#689F38] hover:to-[#0097A7] text-white font-semibold py-4 text-lg"
              >
                {loading ? (
                  <>Envoi en cours...</>
                ) : (
                  <>
                    <Send className="mr-2" size={20} />
                    Envoyer ma candidature
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Vous avez des questions sur le partenariat ?</p>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <a href="mailto:contact@ecopra.ch" className="flex items-center gap-2 text-[#00BCD4] hover:underline">
              <Mail size={18} />
              contact@ecopra.ch
            </a>
            <a href="tel:+41274568270" className="flex items-center gap-2 text-[#00BCD4] hover:underline">
              <Phone size={18} />
              +41 27 456 82 70
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevenirPartenaire;
